// CC-CEDICT parsing and lookup — pure, and deliberately free of any loader.
//
// *Getting* the dictionary differs per platform: the mobile app resolves a bundled asset through
// expo-asset, while the Admin Console fetches it and caches it in IndexedDB (ADR 0036). Only the
// parsing and lookup below is common, so only that lives here; each client owns its own loader and
// feeds the resulting content string to buildDictionary / buildMultiDict.

export interface DictEntry {
  traditional: string;
  simplified: string;
  pinyin: string;
  definition: string;
}

function parseLine(line: string): { simplified: string; entry: DictEntry } | null {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith('#')) return null;
  const match = trimmed.match(/^(\S+)\s+(\S+)\s+\[([^\]]+)\]\s+\/(.+)\/$/);
  if (!match) return null;
  const [, traditional, simplified, pinyin, defsPart] = match;
  const defs = defsPart.split('/').filter((d) => d.length > 0);
  return {
    simplified,
    entry: { traditional, simplified, pinyin, definition: defs.join('; ') },
  };
}

export function buildDictionary(content: string): Map<string, DictEntry> {
  const dict = new Map<string, DictEntry>();
  // Index by both simplified and traditional form so lookups resolve regardless
  // of which script the text uses. Clone the entry for the traditional key so the
  // definition merge below can't leak a later simplified-keyed edit across forms.
  const add = (key: string, entry: DictEntry) => {
    const existing = dict.get(key);
    if (existing) existing.definition += '; ' + entry.definition;
    else dict.set(key, entry);
  };
  for (const line of content.split('\n')) {
    const result = parseLine(line);
    if (!result) continue;
    const { simplified, traditional } = result.entry;
    add(simplified, result.entry);
    if (traditional !== simplified) add(traditional, { ...result.entry });
  }
  return dict;
}

export function buildMultiDict(content: string): Map<string, DictEntry[]> {
  const dict = new Map<string, DictEntry[]>();
  // Index by both simplified and traditional form so getAllReadings /
  // getCharReadings resolve regardless of which script the text uses.
  const add = (key: string, entry: DictEntry) => {
    const existing = dict.get(key);
    if (existing) existing.push(entry);
    else dict.set(key, [entry]);
  };
  for (const line of content.split('\n')) {
    const result = parseLine(line);
    if (!result) continue;
    const { simplified, traditional } = result.entry;
    add(simplified, result.entry);
    if (traditional !== simplified) add(traditional, result.entry);
  }
  return dict;
}

export function lookup(dict: Map<string, DictEntry>, simplified: string): DictEntry | undefined {
  return dict.get(simplified);
}

export function lookupWithFallback(
  dict: Map<string, DictEntry>,
  simplified: string,
): DictEntry | undefined {
  const direct = dict.get(simplified);
  if (direct) return direct;
  if (simplified.length <= 1) return undefined;

  const charEntries: DictEntry[] = [];
  for (const char of simplified) {
    const entry = dict.get(char);
    if (entry) charEntries.push(entry);
  }
  if (charEntries.length === 0) return undefined;

  return {
    traditional: charEntries.map((e) => e.traditional).join(''),
    simplified,
    pinyin: charEntries.map((e) => e.pinyin).join(' '),
    definition: charEntries.map((e) => e.definition.split('; ')[0]).join('; '),
  };
}

const TONE_DIACRITICS: Record<string, string[]> = {
  a: ['ā', 'á', 'ǎ', 'à', 'a'],
  e: ['ē', 'é', 'ě', 'è', 'e'],
  i: ['ī', 'í', 'ǐ', 'ì', 'i'],
  o: ['ō', 'ó', 'ǒ', 'ò', 'o'],
  u: ['ū', 'ú', 'ǔ', 'ù', 'u'],
  ü: ['ǖ', 'ǘ', 'ǚ', 'ǜ', 'ü'],
};

function convertSyllable(syllable: string): string {
  let s = syllable.toLowerCase().replace(/u:/g, 'ü');
  const toneMatch = s.match(/([1-5])$/);
  if (!toneMatch) return s;
  const tone = parseInt(toneMatch[1]) - 1; // 0-indexed
  s = s.slice(0, -1);
  if (tone === 4) return s; // tone 5 = neutral, no diacritic
  if (s.includes('a')) return s.replace('a', TONE_DIACRITICS.a[tone]);
  if (s.includes('e')) return s.replace('e', TONE_DIACRITICS.e[tone]);
  if (s.includes('ou')) return s.replace('o', TONE_DIACRITICS.o[tone]);
  const vowels = ['a', 'e', 'i', 'o', 'u', 'ü'];
  let lastIdx = -1;
  let lastVowel = '';
  for (let i = 0; i < s.length; i++) {
    if (vowels.includes(s[i])) { lastIdx = i; lastVowel = s[i]; }
  }
  if (lastIdx === -1) return s;
  return s.slice(0, lastIdx) + TONE_DIACRITICS[lastVowel][tone] + s.slice(lastIdx + 1);
}

export function numberedToToneMarks(pinyin: string): string {
  return pinyin.split(' ').map(convertSyllable).join(' ');
}

export function getAllReadings(multiDict: Map<string, DictEntry[]>, simplified: string): string[] {
  const entries = multiDict.get(simplified) ?? [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const e of entries) {
    const converted = numberedToToneMarks(e.pinyin);
    if (!seen.has(converted)) { seen.add(converted); result.push(converted); }
  }
  return result;
}

export function getCharReadings(multiDict: Map<string, DictEntry[]>, char: string): string[] {
  return getAllReadings(multiDict, char);
}
